import { Badge, Caption1, Popover, PopoverSurface, PopoverTrigger, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { ArrowCounterclockwise16Regular, CalendarCancel16Regular, PersonAdd16Regular, Warning16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { DateField } from "@/components/date-field";
import { FuiButton, FuiDropdown, FuiInput, FuiSearchBox } from "@/components/fui-control";
import type { ISODate, ProjectAccessRole, ProjectResourceAssignment, UserResource } from "@/engine/types";
import { formatDate } from "@/lib/date-format";
import { fmtCurrency, fmtMM } from "@/lib/format";
import { useCatalogStore } from "@/store/catalog-store";
import { assignmentEdge, assignmentMonthMM, clipMonthlyOverrides, estimatePlannedMM, gapMonthsTo, hasMonthOverride, isAssignmentActive, listMonthOverrides, monthBounds, monthsBetween, sumAssignmentMM } from "./portfolio";
import { ACCESS_ROLE_OPTIONS, ALLOCATION_PRESETS, EMPLOYMENT_LABEL } from "./resource-meta";

const useStyles = makeStyles({
  split: {
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    gap: tokens.spacingHorizontalL,
    minHeight: "420px",
  },
  picker: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minHeight: 0,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingHorizontalM,
  },
  list: {
    flex: 1,
    minHeight: 0,
    maxHeight: "380px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `6px ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    border: "1px solid transparent",
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    backgroundColor: "transparent",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  rowOn: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  rowText: { display: "flex", flexDirection: "column", minWidth: 0, flex: 1 },
  ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  person: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  presetGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    width: "fit-content",
    overflow: "hidden",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  presetBtn: {
    minWidth: "52px",
    height: "24px",
    borderRadius: "0px",
    border: "none",
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: "none",
  },
  presetLast: { borderRight: "none" },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  metric: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
  },
  metricValue: { fontVariantNumeric: "tabular-nums", fontSize: tokens.fontSizeBase300 },
  warn: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  warnText: {
    whiteSpace: "nowrap",
  },
  warnAction: {
    marginLeft: "auto",
    flexShrink: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    fontSize: "inherit",
    fontFamily: "inherit",
    lineHeight: "inherit",
    padding: "2px 0",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    ":hover": { color: "inherit" },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
  },
  planList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: "220px",
    maxWidth: "280px",
    maxHeight: "200px",
    overflowY: "auto",
  },
  planRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  planName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  planPct: {
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  info: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },
  exception: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  exceptionList: {
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: "tabular-nums",
  },
});

export type AssignmentDraft = Omit<ProjectResourceAssignment, "id">;

/**
 * Two-pane assignment editor: pick a person from the enterprise pool (with live load), then set
 * role, window and allocation while the M/M and cross-project utilization update instantly.
 */
export function AssignmentDialog({
  open,
  initial,
  preselectResourceId,
  projectId,
  projectStart,
  projectFinish,
  assignedResourceIds,
  masked,
  seedDates,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: ProjectResourceAssignment | null;
  preselectResourceId?: string | null;
  projectId: string;
  projectStart: ISODate;
  projectFinish: ISODate;
  assignedResourceIds: string[];
  masked?: boolean;
  /** Prefill the window (e.g. from a month cell “이 달에 다른 인력 배정”). */
  seedDates?: { start: ISODate; end: ISODate } | null;
  onClose: () => void;
  onSave: (draft: AssignmentDraft) => void;
}) {
  const styles = useStyles();
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const getWorkload = useCatalogStore((s) => s.getResourceWorkload);

  const editing = Boolean(initial);
  const [query, setQuery] = useState("");
  const assigned = useMemo(() => new Set(assignedResourceIds), [assignedResourceIds]);
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(resources)
      .filter((r) => r.isActive !== false)
      .filter((r) => !q || [r.displayName, r.roleTitle, ...r.skills, teams[r.teamId ?? ""]?.name ?? ""].some((s) => s.toLowerCase().includes(q)))
      .sort((a, b) => {
        const la = getWorkload(a.id).totalAllocationRatio;
        const lb = getWorkload(b.id).totalAllocationRatio;
        return la - lb || a.displayName.localeCompare(b.displayName, "ko");
      });
  }, [resources, query, teams, getWorkload]);

  const defaultResourceId =
    initial?.resourceId ?? preselectResourceId ?? candidates.find((r) => !assigned.has(r.id))?.id ?? candidates[0]?.id ?? "";

  const [resourceId, setResourceId] = useState(defaultResourceId);
  const [projectRole, setProjectRole] = useState(initial?.projectRole ?? resources[defaultResourceId]?.roleTitle ?? "");
  const [accessRole, setAccessRole] = useState<ProjectAccessRole>(initial?.accessRole ?? "member");
  const [startDate, setStartDate] = useState<ISODate>(initial?.startDate ?? seedDates?.start ?? projectStart);
  const [endDate, setEndDate] = useState<ISODate>(initial?.endDate ?? seedDates?.end ?? projectFinish);
  const [ratio, setRatio] = useState<number>(initial?.allocationRatio ?? 1);
  const [ratioText, setRatioText] = useState(String(Math.round((initial?.allocationRatio ?? 1) * 100)));
  const [mmOverride, setMmOverride] = useState<string>(initial ? String(initial.plannedMM) : "");
  const [rateOverride, setRateOverride] = useState<string>(initial?.rateOverride?.monthlyRate !== undefined ? String(initial.rateOverride.monthlyRate) : "");
  const [overrides, setOverrides] = useState<Record<string, number> | undefined>(initial?.monthlyOverrides);

  const resource: UserResource | undefined = resources[resourceId];
  const workload = resourceId ? getWorkload(resourceId) : null;
  const otherPlans = useMemo(
    () => (workload ? workload.assignments.filter((a) => a.projectId !== projectId) : []),
    [workload, projectId],
  );
  const otherLoad = useMemo(() => otherPlans.reduce((s, a) => s + a.allocationRatio, 0), [otherPlans]);
  const projectedLoad = otherLoad + ratio;
  const over = projectedLoad > 1.05;

  const autoMM = useMemo(() => estimatePlannedMM(startDate, endDate, ratio), [startDate, endDate, ratio]);
  const clippedOverrides = useMemo(
    () => clipMonthlyOverrides(overrides, startDate, endDate),
    [overrides, startDate, endDate],
  );
  const exceptionRows = useMemo(
    () => listMonthOverrides(clippedOverrides, startDate, endDate),
    [clippedOverrides, startDate, endDate],
  );
  const cellSum = useMemo(
    () => sumAssignmentMM({ startDate, endDate, allocationRatio: ratio, monthlyOverrides: clippedOverrides }),
    [startDate, endDate, ratio, clippedOverrides],
  );
  const hasExceptions = exceptionRows.length > 0;
  const plannedMM = hasExceptions ? cellSum : mmOverride.trim() ? Math.max(0, Number(mmOverride) || 0) : autoMM;
  const rate = rateOverride.trim() ? Number(rateOverride) || 0 : resource?.defaultMonthlyRate ?? 0;
  const laborCost = plannedMM * rate;

  const datesOk = Boolean(startDate && endDate && startDate <= endDate);
  const droppedMonths = useMemo(() => {
    if (!initial || !datesOk) return 0;
    const next = new Set(monthsBetween(startDate, endDate));
    return monthsBetween(initial.startDate, initial.endDate).filter((ym) => !next.has(ym)).length;
  }, [initial, datesOk, startDate, endDate]);
  const valid = Boolean(resourceId) && projectRole.trim().length > 0 && datesOk && ratio > 0;

  useEffect(() => {
    if (!open || initial) return;
    const id = preselectResourceId ?? candidates.find((r) => !assigned.has(r.id))?.id ?? candidates[0]?.id;
    if (!id) return;
    setResourceId(id);
    const r = resources[id];
    if (r) setProjectRole((role) => (role.trim() ? role : r.roleTitle));
    // 검색으로 목록이 바뀌어도 선택을 유지하고, 모달을 열 때만 첫 항목을 고른다.
  }, [open]);

  const pick = (id: string) => {
    setResourceId(id);
    const r = resources[id];
    if (r && !projectRole.trim()) setProjectRole(r.roleTitle);
  };

  const applyRatio = (v: number) => {
    const clamped = Math.min(2, Math.max(0.05, v));
    setRatio(clamped);
    setRatioText(String(Math.round(clamped * 100)));
  };

  const submit = () => {
    if (!valid) return;
    const draft: AssignmentDraft = {
      resourceId,
      projectRole: projectRole.trim(),
      accessRole,
      startDate,
      endDate,
      allocationRatio: ratio,
      plannedMM: Math.round(plannedMM * 10) / 10,
      monthlyOverrides: clippedOverrides,
      ...(rateOverride.trim() ? { rateOverride: { ...initial?.rateOverride, monthlyRate: Number(rateOverride) || 0 } } : {}),
    };
    onSave(draft);
  };

  const form = (
    <div className={styles.form}>
      {resource ? (
        <div className={styles.person}>
          <PersonAvatar name={resource.displayName} size={32} />
          <div className={styles.rowText}>
            <span style={{ fontSize: 13 }}>{resource.displayName}</span>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {resource.roleTitle} · {EMPLOYMENT_LABEL[resource.employmentType]}
              {teams[resource.teamId ?? ""] ? ` · ${teams[resource.teamId ?? ""]!.name}` : ""}
            </Caption1>
          </div>
          {workload && workload.projectCount > 0 && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }}>
              타 플랜 {Math.round(otherLoad * 100)}%
            </Caption1>
          )}
        </div>
      ) : (
        <div className={styles.empty} style={{ minHeight: 56 }}>
          <Caption1>왼쪽에서 배정할 인력을 선택하세요.</Caption1>
        </div>
      )}

      <DialogGrid2>
        <DialogField label="플랜 내 역할" required>
          <FuiInput density="dialog" value={projectRole} onChange={(_, d) => setProjectRole(d.value)} placeholder="예: 공통 프레임워크 PL" disabled={!resource} />
        </DialogField>
        <DialogField label="권한">
          <FuiDropdown density="dialog" value={accessRole} options={ACCESS_ROLE_OPTIONS} onValueChange={(v) => setAccessRole(v as ProjectAccessRole)} disabled={!resource} />
        </DialogField>
      </DialogGrid2>

      <DialogGrid2>
        <DialogField label="투입 시작">
          <DateField size="sm" value={startDate} onChange={(v) => v && setStartDate(v)} aria-label="투입 시작" />
        </DialogField>
        <DialogField
          label="투입 종료"
          hint={
            !datesOk
              ? "종료일이 시작일보다 앞설 수 없습니다"
              : droppedMonths > 0
                ? `기간 밖 달 ${droppedMonths}개는 빠집니다.`
                : undefined
          }
        >
          <DateField size="sm" value={endDate} onChange={(v) => v && setEndDate(v)} aria-label="투입 종료" />
        </DialogField>
      </DialogGrid2>

      <DialogField label="기본 투입률" hint="달을 따로 바꾸지 않으면 이 비율이 들어갑니다">
        <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexWrap: "wrap" }}>
          <div className={styles.presetGroup} role="group" aria-label="투입률 프리셋">
            {ALLOCATION_PRESETS.map((p, i, all) => (
              <FuiButton
                key={p.value}
                density="dialog"
                className={mergeClasses(styles.presetBtn, i === all.length - 1 && styles.presetLast)}
                appearance={Math.abs(ratio - p.value) < 0.001 ? "primary" : "subtle"}
                onClick={() => applyRatio(p.value)}
                disabled={!resource}
              >
                {p.label}
              </FuiButton>
            ))}
          </div>
          <FuiInput
            density="dialog"
            style={{ width: 88 }}
            value={ratioText}
            inputMode="numeric"
            contentAfter="%"
            aria-label="투입률 직접 입력"
            disabled={!resource}
            onChange={(_, d) => setRatioText(d.value)}
            onBlur={() => applyRatio((Number(ratioText) || 100) / 100)}
            onKeyDown={(e) => e.key === "Enter" && applyRatio((Number(ratioText) || 100) / 100)}
          />
        </div>
      </DialogField>

      {hasExceptions && (
        <div className={styles.exception}>
          <Caption1 className={styles.exceptionList}>
            월별 예외 {exceptionRows.length}개 · {exceptionRows.map((r) => `${formatDate(r.ym, "yearMonthShort")} ${r.value.toFixed(1)}`).join(" · ")}
          </Caption1>
          <FuiButton
            density="dialog"
            appearance="subtle"
            onClick={() => {
              setOverrides(undefined);
              setMmOverride("");
            }}
          >
            예외를 지우고 일괄 비율로
          </FuiButton>
        </div>
      )}

      <DialogGrid2>
        <DialogField
          label="계획 M/M"
          hint={
            hasExceptions
              ? `월별 예외가 있어 칸 합 ${fmtMM(cellSum)}을 씁니다`
              : "미입력 시 기간·투입률로 자동 계산"
          }
        >
          <FuiInput
            density="dialog"
            type="number"
            step={0.1}
            value={hasExceptions ? String(cellSum) : mmOverride}
            placeholder={String(autoMM)}
            onChange={(_, d) => setMmOverride(d.value)}
            disabled={!resource || hasExceptions}
            contentAfter="M/M"
          />
        </DialogField>
        {!masked && (
          <DialogField label="월 단가 (현재 플랜 전용)" hint={resource?.defaultMonthlyRate ? `기본 ${fmtCurrency(resource.defaultMonthlyRate)}` : "기본 단가 없음"}>
            <FuiInput density="dialog" type="number" step={100000} value={rateOverride} placeholder={resource?.defaultMonthlyRate ? String(resource.defaultMonthlyRate) : "0"} onChange={(_, d) => setRateOverride(d.value)} disabled={!resource} contentBefore="₩" />
          </DialogField>
        )}
      </DialogGrid2>

      <div className={styles.summary}>
        <div className={styles.metric}>
          <Caption1>계획 공수</Caption1>
          <span className={styles.metricValue}>{fmtMM(plannedMM)}</span>
        </div>
        <div className={styles.metric}>
          <Caption1>예상 인건비</Caption1>
          <span className={styles.metricValue}>{fmtCurrency(laborCost, masked)}</span>
        </div>
        <div className={styles.metric}>
          <Caption1>배정 후 총 가동률</Caption1>
          <span className={styles.metricValue} style={{ color: over ? tokens.colorPaletteRedForeground1 : undefined }}>
            {resource ? `${Math.round(projectedLoad * 100)}%` : "—"}
          </span>
        </div>
      </div>

      {resource && otherPlans.length > 0 && (
        <div className={mergeClasses(styles.warn, !over && styles.info)}>
          <Warning16Regular style={{ flexShrink: 0 }} />
          <span className={styles.warnText}>
            {over ? "100%를 넘습니다. 투입률이나 기간을 조정하세요." : "다른 플랜을 포함해도 100% 이내입니다."}
          </span>
          <Popover positioning="above-end">
            <PopoverTrigger disableButtonEnhancement>
              <button type="button" className={styles.warnAction}>
                참여 중인 플랜 {otherPlans.length}
              </button>
            </PopoverTrigger>
            <PopoverSurface>
              <div className={styles.planList} role="list" aria-label="현재 플랜을 제외한 참여 플랜">
                {otherPlans.map((a) => (
                  <div key={`${a.projectId}-${a.startDate}`} className={styles.planRow} role="listitem">
                    <span className={styles.planName} title={a.projectName}>{a.projectName}</span>
                    <span className={styles.planPct}>{Math.round(a.allocationRatio * 100)}%</span>
                  </div>
                ))}
              </div>
            </PopoverSurface>
          </Popover>
        </div>
      )}
    </div>
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width={editing ? "lg" : "xl"}
      title={editing ? "인력 배정 수정" : "플랜에 인력 배정"}
      footerStart={
        !valid ? (
          <Caption1>{!resourceId ? "인력을 선택하세요" : !projectRole.trim() ? "플랜 내 역할을 입력하세요" : !datesOk ? "기간을 확인하세요" : "투입률을 입력하세요"}</Caption1>
        ) : hasExceptions ? (
          <Caption1>월별 예외 {exceptionRows.length}개는 유지됩니다</Caption1>
        ) : null
      }
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} confirmLabel={editing ? "저장" : "배정"} disabled={!valid} />}
    >
      {editing ? (
        form
      ) : (
        <div className={styles.split}>
          <div className={styles.picker}>
            <FuiSearchBox density="dialog" value={query} onChange={(_, d) => setQuery(d.value)} placeholder="이름, 역할, 기술, 팀 검색" style={{ width: "100%" }} />
            <div className={styles.list}>
              {candidates.map((r) => {
                const wl = getWorkload(r.id);
                const pct = Math.round(wl.totalAllocationRatio * 100);
                const already = assigned.has(r.id);
                return (
                  <button key={r.id} type="button" disabled={already} className={mergeClasses(styles.row, r.id === resourceId && styles.rowOn)} onClick={() => pick(r.id)}>
                    <PersonAvatar name={r.displayName} size={28} tip={false} />
                    <span className={styles.rowText}>
                      <span className={styles.ellipsis} style={{ fontSize: 13 }}>
                        {r.displayName}
                        {already && (
                          <Badge size="small" appearance="tint" color="brand" style={{ marginLeft: 6 }}>
                            배정됨
                          </Badge>
                        )}
                      </span>
                      <Caption1 className={styles.ellipsis} style={{ color: tokens.colorNeutralForeground3 }}>
                        {r.roleTitle}
                      </Caption1>
                    </span>
                    <span style={{ flexShrink: 0, color: wl.isOverAllocated ? tokens.colorPaletteRedForeground1 : pct === 0 ? tokens.colorNeutralForeground4 : tokens.colorNeutralForeground2 }}>{pct}%</span>
                  </button>
                );
              })}
            </div>
          </div>
          {form}
        </div>
      )}
    </AppDialog>
  );
}

const useMonthStyles = makeStyles({
  subject: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  subjectText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  person: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  role: {
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
});

function fmtMonthTitle(ym: string): string {
  return formatDate(ym, "yearMonth");
}

function clampMonthMMText(raw: string): string {
  if (raw.trim() === "" || raw === "." || raw === "-") return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  if (n > 1) return "1.0";
  if (n < 0) return "0";
  return raw;
}

export function MonthCellDialog({
  open,
  assignment,
  ym,
  personName,
  onClose,
  onSave,
  onShrink,
  onAssignOther,
}: {
  open: boolean;
  assignment: ProjectResourceAssignment;
  ym: string;
  personName: string;
  onClose: () => void;
  onSave: (value: number | null) => void;
  onShrink: () => void;
  onAssignOther: (seed: { start: ISODate; end: ISODate }) => void;
}) {
  const styles = useMonthStyles();
  const baseline = assignment.allocationRatio;
  const active = isAssignmentActive(assignment, ym);
  const current = active ? assignmentMonthMM(assignment, ym) : baseline;
  const overridden = active && hasMonthOverride(assignment, ym);
  const edge = active ? assignmentEdge(assignment, ym) : null;
  const canShrink = edge === "start" || edge === "end";
  const gap = active ? 0 : gapMonthsTo(assignment, ym);
  const [text, setText] = useState(current.toFixed(1));
  const [confirmShrink, setConfirmShrink] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const next = isAssignmentActive(assignment, ym) ? assignmentMonthMM(assignment, ym) : assignment.allocationRatio;
    setText(next.toFixed(1));
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, assignment, ym]);

  const parsed = Number(text);
  const valid = text.trim() !== "" && Number.isFinite(parsed) && parsed >= 0 && parsed <= 1;
  const commit = () => {
    if (!valid) return;
    onSave(Math.min(1, Math.max(0, Math.round(parsed * 10) / 10)));
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="xs"
      title={fmtMonthTitle(ym)}
      footer={<DialogActionsBar onCancel={onClose} onConfirm={commit} confirmLabel="저장" disabled={!valid} />}
    >
      <div className={styles.subject}>
        <PersonAvatar name={personName} size={28} />
        <div className={styles.subjectText}>
          <span className={styles.person}>{personName}</span>
          <Caption1 className={styles.role}>{assignment.projectRole}</Caption1>
        </div>
      </div>
      <DialogField
        label="투입"
        hint={
          !active
            ? gap > 0
              ? "이 달을 기간에 넣습니다. 사이 달은 기본 투입으로 채워집니다."
              : "이 달을 투입 기간에 넣습니다."
            : undefined
        }
      >
        <FuiInput
          ref={inputRef}
          density="dialog"
          type="number"
          step={0.1}
          min={0}
          max={1}
          value={text}
          onChange={(_, d) => setText(clampMonthMMText(d.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
          contentAfter="M/M"
          aria-label={`${personName} ${formatDate(ym, "yearMonth")} 투입 M/M`}
        />
      </DialogField>
      <div className={styles.actions}>
        {overridden && (
          <FuiButton density="dialog" appearance="secondary" icon={<ArrowCounterclockwise16Regular />} onClick={() => onSave(null)}>
            기본 투입
          </FuiButton>
        )}
        {canShrink && (
          <FuiButton density="dialog" appearance="secondary" icon={<CalendarCancel16Regular />} onClick={() => setConfirmShrink(true)}>
            기간에서 빼기
          </FuiButton>
        )}
        <FuiButton density="dialog" appearance="subtle" icon={<PersonAdd16Regular />} onClick={() => onAssignOther(monthBounds(ym))}>
          다른 인력 배정
        </FuiButton>
      </div>
      <ConfirmDialog
        open={confirmShrink}
        title="기간에서 빼기"
        message="이 달을 투입 기간에서 뺄까요? 이 달의 투입 M/M도 함께 사라집니다."
        confirmLabel="빼기"
        onCancel={() => setConfirmShrink(false)}
        onConfirm={() => {
          setConfirmShrink(false);
          onShrink();
        }}
      />
    </AppDialog>
  );
}
